import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useApiClient } from '@rudi/api/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@rudi/ui/button';
import { EmailListField } from '@rudi/ui/email-list-field';
import { Input } from '@rudi/ui/input';
import { RolePicker, type RoleCategory } from '@rudi/ui/role-picker';
import { Switch } from '@rudi/ui/switch';
import { toast } from 'sonner';
import { QueryState } from '../components/page';
import { useSetUserInternal } from '../api/hooks';
import type { AdminUser, UserAffiliation } from '../api/types';

function errorMessage(error: unknown): string {
  const data = (error as { response?: { data?: { detail?: unknown; error?: unknown } } })?.response
    ?.data;
  const message = data?.detail ?? data?.error;
  return typeof message === 'string' ? message : 'Could not save changes. Please try again.';
}

export function userAffiliations(user: AdminUser): UserAffiliation[] {
  return (
    user.affiliations ??
    (user.tenant_id
      ? [
          {
            tenant_id: user.tenant_id,
            tenant_name: user.tenant_name ?? 'Tenant',
            role_names: user.role_names,
            teams: [],
          },
        ]
      : [])
  );
}

export function UserProfilePanel({ user }: { user: AdminUser }) {
  const api = useApiClient();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user.display_name ?? '');
  const [email, setEmail] = useState(user.email ?? '');
  const save = useMutation({
    mutationFn: () =>
      api.patch(`/admin/users/${user.id}/profile`, {
        display_name: name.trim(),
        email: email.trim(),
      }),
    onSuccess: async () => {
      await Promise.all(
        ['users', 'tenant-members', 'tenant-access', 'team-members'].map(key =>
          qc.invalidateQueries({ queryKey: [key] })
        )
      );
      setEditing(false);
      toast.success('Profile saved');
    },
  });
  return (
    <section
      className="max-w-2xl space-y-5 rounded-3xl border bg-card p-6"
      aria-label="User profile"
    >
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-medium">Profile</h2>
        {!editing && (
          <Button
            variant="outline"
            onClick={() => {
              setName(user.display_name ?? '');
              setEmail(user.email ?? '');
              save.reset();
              setEditing(true);
            }}
          >
            Edit profile
          </Button>
        )}
      </div>
      <form
        aria-label="Edit user profile"
        className="space-y-4"
        onSubmit={event => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <label className="block space-y-1 text-sm">
          Display name
          <Input
            value={editing ? name : (user.display_name ?? '')}
            onChange={event => setName(event.target.value)}
            readOnly={!editing}
            disabled={save.isPending}
            required
            maxLength={200}
            autoComplete="name"
          />
        </label>
        <label className="block space-y-1 text-sm">
          Email
          <Input
            type="email"
            value={editing ? email : (user.email ?? '')}
            onChange={event => setEmail(event.target.value)}
            readOnly={!editing}
            disabled={save.isPending}
            required
            autoComplete="email"
          />
        </label>
        {editing && (
          <p className="text-sm text-muted-foreground">
            A changed email must be verified at the next sign-in. Tenant and team memberships stay
            the same.
          </p>
        )}
        <dl className="space-y-3 text-sm">
          <div>
            <dt className="text-muted-foreground">Username</dt>
            <dd>{user.username}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Last login</dt>
            <dd>
              {user.last_login ? new Date(user.last_login).toLocaleString() : 'No login recorded'}
            </dd>
          </div>
        </dl>
        {editing && (
          <>
            {save.error && (
              <p role="alert" className="text-sm text-destructive">
                {errorMessage(save.error)}
              </p>
            )}
            <div className="flex gap-2">
              <Button type="submit" disabled={save.isPending || !name.trim() || !email.trim()}>
                {save.isPending ? 'Saving…' : 'Save profile'}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={save.isPending}
                onClick={() => setEditing(false)}
              >
                Cancel
              </Button>
            </div>
          </>
        )}
      </form>
    </section>
  );
}

function internalErrorMessage(error: unknown): string {
  const status = (error as { response?: { status?: number } })?.response?.status;
  // A Rudi from before Product health (ADR 0086) has no such route.
  if (status === 404 || status === 405 || status === 501)
    return 'This Rudi cannot mark internal users yet. Release a newer Rudi first.';
  return errorMessage(error);
}

/**
 * Marks Nightingale's own staff and test accounts, whom Rudi's Product health
 * leaves out. Rudi releases before it report no `is_internal`, so this shows
 * nothing there.
 */
export function UserInternalPanel({ user }: { user: AdminUser }) {
  const setInternal = useSetUserInternal();
  if (user.is_internal === undefined) return null;
  const onChange = async (internal: boolean) => {
    try {
      await setInternal.mutateAsync({ userId: user.id, internal });
      toast.success(internal ? 'Marked as internal user' : 'No longer marked as internal user');
    } catch (error) {
      toast.error(internalErrorMessage(error));
    }
  };
  return (
    <section
      className="max-w-2xl space-y-3 rounded-3xl border bg-card p-6"
      aria-label="Internal user"
    >
      <div className="flex items-center justify-between gap-4">
        <div className="space-y-1">
          <h2 className="text-lg font-medium" id={`internal-${user.id}`}>
            Internal user
          </h2>
          <p className="text-sm text-muted-foreground">
            Mark Nightingale's own staff and test accounts. Product health statistics leave out
            their activity, AI cost and feedback.
          </p>
        </div>
        <Switch
          aria-labelledby={`internal-${user.id}`}
          checked={user.is_internal}
          disabled={setInternal.isPending}
          onCheckedChange={checked => void onChange(checked)}
        />
      </div>
    </section>
  );
}

export function UserSecurityPanel({ user }: { user: AdminUser }) {
  const api = useApiClient();
  const emailReset = useMutation({
    mutationFn: () => api.post(`/admin/users/${user.id}/password-reset-email`),
    onSuccess: () => {
      toast.success('Password reset email sent');
    },
  });
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const reset = useMutation({
    mutationFn: async () =>
      (
        await api.put<{ success: boolean; setup_complete?: boolean; notification_sent?: boolean }>(
          `/admin/users/${user.id}/password`,
          { password }
        )
      ).data,
    onSuccess: result => {
      setPassword('');
      setConfirmation('');
      if (result.setup_complete === false)
        toast.warning('Password changed, but account setup could not be completed.');
      else if (result.notification_sent === false)
        toast.warning('Password set. The notification email could not be sent.');
      else toast.success('Password set');
    },
  });
  const mismatch = confirmation.length > 0 && password !== confirmation;
  return (
    <section className="max-w-2xl space-y-4 rounded-3xl border bg-card p-6">
      <h2 className="text-lg font-medium">Password</h2>
      <p className="text-sm text-muted-foreground">
        Send a password reset link to {user.email || 'this user'}. The link expires after 30
        minutes. Their password stays the same until they choose a new one.
      </p>
      {!user.email && (
        <p className="text-sm text-muted-foreground">Add an email address in Profile first.</p>
      )}
      <Button
        disabled={!user.email || emailReset.isPending || reset.isPending}
        onClick={() => emailReset.mutate()}
      >
        {emailReset.isPending ? 'Sending…' : 'Send password reset email'}
      </Button>
      {emailReset.error && (
        <p role="alert" className="text-sm text-destructive">
          {errorMessage(emailReset.error)}
        </p>
      )}
      {emailReset.isSuccess && (
        <p role="status" className="text-sm">
          Password reset email sent to {user.email}.
        </p>
      )}
      <details open className="border-t pt-4">
        <summary className="cursor-pointer text-sm font-medium">Set a password</summary>
        <p className="my-4 text-sm text-muted-foreground">
          Set a permanent password the user can use immediately. This also confirms their email
          address and clears pending password-change and email-verification steps. Other account
          restrictions still apply. The user receives a password reset notice with no password or
          setup link. Email delivery is not required to sign in.
        </p>
        <form
          aria-label="Reset user password"
          className="space-y-4"
          onSubmit={event => {
            event.preventDefault();
            if (
              user.email &&
              !emailReset.isPending &&
              !reset.isPending &&
              password === confirmation &&
              password.length >= 8
            ) {
              emailReset.reset();
              reset.mutate();
            }
          }}
        >
          <label className="block space-y-1 text-sm">
            New password
            <Input
              type="password"
              autoComplete="new-password"
              minLength={8}
              maxLength={1024}
              required
              value={password}
              disabled={reset.isPending}
              onChange={event => {
                reset.reset();
                setPassword(event.target.value);
              }}
            />
          </label>
          <p className="text-xs text-muted-foreground">
            Use at least 8 characters. The account's password policy also applies.
          </p>
          <label className="block space-y-1 text-sm">
            Confirm password
            <Input
              type="password"
              autoComplete="new-password"
              required
              value={confirmation}
              disabled={reset.isPending}
              onChange={event => setConfirmation(event.target.value)}
              aria-invalid={mismatch}
            />
          </label>
          {mismatch && (
            <p role="alert" className="text-sm text-destructive">
              The passwords do not match.
            </p>
          )}
          {reset.error && (
            <p role="alert" className="text-sm text-destructive">
              {errorMessage(reset.error)}
            </p>
          )}
          {reset.isSuccess && (
            <p role={reset.data?.setup_complete === false ? 'alert' : 'status'} className="text-sm">
              {reset.data?.setup_complete === false
                ? 'Password changed, but account setup could not be completed. Set the password again to retry before asking the user to sign in.'
                : 'Password set. No password change or email verification is required to use it.'}
            </p>
          )}
          {reset.isSuccess && reset.data?.notification_sent !== undefined && (
            <p role={reset.data.notification_sent ? 'status' : 'alert'} className="text-sm">
              {reset.data.notification_sent
                ? 'Password reset notification sent to the user.'
                : 'The notification email could not be sent. This does not prevent sign-in with the new password.'}
            </p>
          )}
          <Button
            type="submit"
            disabled={
              !user.email ||
              emailReset.isPending ||
              reset.isPending ||
              password.length < 8 ||
              password !== confirmation
            }
          >
            {reset.isPending ? 'Setting password…' : 'Set password'}
          </Button>
        </form>
      </details>
    </section>
  );
}

interface Role {
  id: string;
  name: string;
  displayName: string;
  description?: string | null;
  category?: RoleCategory;
  isBuiltin?: boolean;
  isActive: boolean;
}
interface Member {
  userId: string;
  /** Missing on Rudi releases that keep one address, set on the team page. */
  additionalEmails?: string[];
  roleIds: string[];
  roles: Role[];
  effectivePermissions: Record<string, string[]>;
}

function TenantUserRoles({
  userId,
  affiliation,
}: {
  userId: string;
  affiliation: UserAffiliation;
}) {
  const api = useApiClient();
  const qc = useQueryClient();
  const tenantId = affiliation.tenant_id;
  const base = `/access/tenants/${tenantId}`;
  const catalog = useQuery({
    queryKey: ['tenant-access', tenantId, 'roles'],
    queryFn: async () => (await api.get<{ roles: Role[] }>(`${base}/roles`)).data,
  });
  const members = useQuery({
    queryKey: ['tenant-access', tenantId, 'members'],
    queryFn: async () => (await api.get<Member[]>(`${base}/members`)).data,
  });
  const member = members.data?.find(row => row.userId === userId);
  const [selected, setSelected] = useState<string[] | null>(null);
  const save = useMutation({
    mutationFn: () => api.put(`${base}/members/${userId}/roles`, { roleIds: selected }),
    onSuccess: async () => {
      await Promise.all(
        [
          'tenant-access',
          'tenant-members',
          'users',
          'user-features',
          'user-deletion-impact',
          'tenants',
        ].map(key => qc.invalidateQueries({ queryKey: [key] }))
      );
      setSelected(null);
      toast.success('Roles saved');
    },
  });
  return (
    <section className="space-y-4 rounded-3xl border bg-card p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-medium">{affiliation.tenant_name}</h2>
        <Button variant="outline" asChild>
          <Link to="/tenants/$tenantId/access" params={{ tenantId }}>
            Manage tenant roles
          </Link>
        </Button>
      </div>
      <QueryState
        isLoading={catalog.isLoading || members.isLoading}
        error={catalog.error || members.error}
      >
        {member ? (
          <>
            <p className="text-sm">
              {member.roles
                .map(role => `${role.displayName}${role.isActive ? '' : ' · Inactive'}`)
                .join(', ') || 'No roles assigned'}
            </p>
            {selected === null ? (
              <Button
                variant="outline"
                onClick={() => {
                  save.reset();
                  setSelected(member.roleIds);
                }}
              >
                Edit roles
              </Button>
            ) : (
              <form
                aria-label={`Roles in ${affiliation.tenant_name}`}
                className="space-y-4"
                onSubmit={event => {
                  event.preventDefault();
                  save.mutate();
                }}
              >
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    Select the roles this user needs in this tenant.
                  </p>
                  <RolePicker
                    label={`Roles in ${affiliation.tenant_name}`}
                    roles={(catalog.data?.roles ?? []).filter(
                      role =>
                        role.name !== 'team_admin' && (role.isActive || selected.includes(role.id))
                    )}
                    value={selected}
                    onChange={setSelected}
                    disabled={save.isPending}
                  />
                </div>
                {save.error && (
                  <p role="alert" className="text-sm text-destructive">
                    {errorMessage(save.error)}
                  </p>
                )}
                <div className="flex gap-2">
                  <Button type="submit" disabled={save.isPending || selected.length === 0}>
                    {save.isPending ? 'Saving…' : 'Save roles'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={save.isPending}
                    onClick={() => setSelected(null)}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}
            <details className="text-sm">
              <summary className="cursor-pointer">Effective permissions</summary>
              <ul className="mt-3 space-y-1">
                {Object.entries(member.effectivePermissions).map(([permission, sources]) => (
                  <li key={permission}>
                    {permission} · {sources.join(', ')}
                  </li>
                ))}
              </ul>
            </details>
          </>
        ) : (
          <p role="status">This user is no longer an active member of this tenant.</p>
        )}
      </QueryState>
    </section>
  );
}

const MAX_ADDITIONAL_EMAILS = 10;

function TenantAdditionalEmails({
  user,
  affiliation,
}: {
  user: AdminUser;
  affiliation: UserAffiliation;
}) {
  const api = useApiClient();
  const qc = useQueryClient();
  const tenantId = affiliation.tenant_id;
  const base = `/access/tenants/${tenantId}`;
  const members = useQuery({
    queryKey: ['tenant-access', tenantId, 'members'],
    queryFn: async () => (await api.get<Member[]>(`${base}/members`)).data,
  });
  const member = members.data?.find(row => row.userId === user.id);
  const [draft, setDraft] = useState<string[] | null>(null);
  const save = useMutation({
    mutationFn: (additionalEmails: string[]) =>
      api.put(`${base}/members/${user.id}/emails`, { additionalEmails }),
    onSuccess: async () => {
      await Promise.all(
        ['tenant-access', 'tenant-members', 'team-members', 'users'].map(key =>
          qc.invalidateQueries({ queryKey: [key] })
        )
      );
      setDraft(null);
      toast.success('Additional emails saved');
    },
  });
  const saved = member?.additionalEmails;
  const emails = draft ?? saved ?? [];
  const changed = draft !== null && draft.join('\n') !== (saved ?? []).join('\n');
  return (
    <section
      className="space-y-3 rounded-3xl border p-4"
      aria-label={`Additional emails in ${affiliation.tenant_name}`}
    >
      <h3 className="font-medium">{affiliation.tenant_name}</h3>
      <QueryState isLoading={members.isLoading} error={members.error}>
        {!member ? (
          <p role="status" className="text-sm">
            This user is no longer an active member of this tenant.
          </p>
        ) : saved === undefined ? (
          <p role="status" className="text-sm text-muted-foreground">
            This Rudi release keeps one additional email per member. Set it on the team page
            {affiliation.teams.length > 0 && (
              <>
                {': '}
                {affiliation.teams.map((team, index) => (
                  <span key={team.id}>
                    {index > 0 && ', '}
                    <Link
                      to="/tenants/$tenantId/teams/$teamId"
                      params={{ tenantId, teamId: team.id }}
                      className="underline underline-offset-4"
                    >
                      {team.name}
                    </Link>
                  </span>
                ))}
              </>
            )}
            .
          </p>
        ) : (
          <form
            className="space-y-3"
            onSubmit={event => {
              event.preventDefault();
              if (changed) save.mutate(emails);
            }}
          >
            <EmailListField
              label={`Additional emails in ${affiliation.tenant_name}`}
              value={emails}
              onChange={setDraft}
              signInEmail={user.email ?? undefined}
              max={MAX_ADDITIONAL_EMAILS}
              disabled={save.isPending}
            />
            {save.error && (
              <p role="alert" className="text-sm text-destructive">
                {errorMessage(save.error)}
              </p>
            )}
            {changed && (
              <div className="flex gap-2">
                <Button type="submit" disabled={save.isPending}>
                  {save.isPending ? 'Saving…' : 'Save emails'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={save.isPending}
                  onClick={() => {
                    save.reset();
                    setDraft(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            )}
          </form>
        )}
      </QueryState>
    </section>
  );
}

/** Other addresses that count for PI identity, kept per tenant membership. */
export function UserAdditionalEmailsPanel({ user }: { user: AdminUser }) {
  const affiliations = userAffiliations(user);
  if (affiliations.length === 0) return null;
  return (
    <section className="max-w-2xl space-y-4 rounded-3xl border bg-card p-6">
      <div className="space-y-1">
        <h2 className="text-lg font-medium">Additional emails</h2>
        <p className="text-sm text-muted-foreground">
          Other addresses this person is known by, such as their entries in CTIS, DRKS or
          ClinicalTrials.gov. Each counts for principal investigator identity like the sign-in
          email. When a principal investigator is found under one of them, their studies join the
          team and the team can edit them.
        </p>
      </div>
      {affiliations.map(affiliation => (
        <TenantAdditionalEmails key={affiliation.tenant_id} user={user} affiliation={affiliation} />
      ))}
    </section>
  );
}

export function UserRolesPanel({ user }: { user: AdminUser }) {
  const affiliations = userAffiliations(user);
  return (
    <div className="max-w-3xl space-y-4">
      {user.is_super_admin && (
        <p className="text-sm text-muted-foreground">
          This user is a platform super admin. Tenant roles do not limit their platform access.
        </p>
      )}
      {affiliations.length ? (
        affiliations.map(affiliation => (
          <TenantUserRoles key={affiliation.tenant_id} userId={user.id} affiliation={affiliation} />
        ))
      ) : (
        <p className="text-sm text-muted-foreground">This user has no active tenant memberships.</p>
      )}
    </div>
  );
}
